import { useState, cloneElement, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Loader2, CheckCircle2, Users, MapPin } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { format } from "date-fns";

const SERVICE_OPTIONS = [
  { value: "one_to_one", label: "1-to-1 Coaching" },
  { value: "schools", label: "Schools Coaching" },
  { value: "haf", label: "HAF Programme" },
];

const TIME_SLOTS = ["09:00", "10:00", "11:00", "14:00", "16:00", "17:00", "18:00"];
const CAMP_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday"];
const DAILY_RATE = 25;
const FULL_WEEK_RATE = 80;
const SIBLING_DISCOUNT = 0.10;

// 1-to-1 venue rota — Monday/Wednesday at Bedford Terrace, Tuesday/Thursday at Bede College.
const VENUE_BY_WEEKDAY = {
  1: "Bedford Terrace, Billingham", // Monday
  2: "Bede College, Billingham",    // Tuesday
  3: "Bedford Terrace, Billingham", // Wednesday
  4: "Bede College, Billingham",    // Thursday
};
const venueForDate = (d) => (d ? VENUE_BY_WEEKDAY[d.getDay()] : null);

const INITIAL_FORM = (defaultService) => ({
  parent_first_name: "",
  parent_last_name: "",
  child_first_name: "",
  child_last_name: "",
  child_age: "",
  email: "",
  phone: "",
  service_type: defaultService,
  preferred_time: "",
  payment_method: "online",
  notes: "",
  booking_option: "full_week",
  num_children: 1,
  additional_children: "",
});

export default function BookingDialog({ trigger, defaultService = "one_to_one", campId = null, campTitle = null, defaultDate = null, defaultTime = "", coachName = "" }) {
  const [open, setOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [date, setDate] = useState(defaultDate);
  const [selectedDays, setSelectedDays] = useState([]);
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM(defaultService),
    preferred_time: defaultTime || "",
    notes: coachName ? `Preferred coach: ${coachName}` : "",
  }));

  const isCamp = form.service_type === "holiday_camp";

  // If no campId was passed in (generic "Book Now"), fetch upcoming camps so the parent can pick a week
  const [camps, setCamps] = useState([]);
  const [pickedCampId, setPickedCampId] = useState(campId);
  useEffect(() => { setPickedCampId(campId); }, [campId]);
  useEffect(() => {
    if (!isCamp || campId) return;
    let cancelled = false;
    apiClient.get("/camps").then(({ data }) => {
      if (cancelled) return;
      setCamps(data || []);
      // Default to first upcoming week if none chosen yet
      if (!pickedCampId && data && data.length) setPickedCampId(data[0].id);
    }).catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCamp, campId]);
  const effectiveCampId = campId || pickedCampId;
  const selectedCamp = useMemo(() => camps.find((c) => c.id === pickedCampId) || null, [camps, pickedCampId]);

  // Fetch coach availability so the time picker only offers real, coachable slots
  const [availSlots, setAvailSlots] = useState([]);
  useEffect(() => {
    if (form.service_type !== "one_to_one" && form.service_type !== "small_group") return;
    let cancelled = false;
    apiClient.get("/availability").then(({ data }) => {
      if (!cancelled) setAvailSlots(data.slots || []);
    }).catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [form.service_type]);

  const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Times available on the picked date, filtered by preferred coach if one was set via chip click
  const availableTimes = useMemo(() => {
    if (isCamp || !date) return [];
    const dayName = WEEKDAY_NAMES[date.getDay()];
    const matching = availSlots.filter((s) => {
      if (s.day !== dayName) return false;
      if (coachName) return s.coach === coachName;
      return true;
    });
    const set = new Set();
    matching.forEach((s) => (s.times || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [isCamp, date, availSlots, coachName]);

  // If the user changes the date and the previously-selected time isn't available, clear it
  useEffect(() => {
    if (isCamp) return;
    if (form.preferred_time && availableTimes.length && !availableTimes.includes(form.preferred_time)) {
      setForm((f) => ({ ...f, preferred_time: "" }));
    }
  }, [date, availableTimes, isCamp, form.preferred_time]);

  const { total, breakdown, hasDiscount } = useMemo(() => {
    if (!isCamp) return { total: null, breakdown: "", hasDiscount: false };
    const perChild = form.booking_option === "full_week"
      ? FULL_WEEK_RATE
      : DAILY_RATE * selectedDays.length;
    const subtotal = perChild * form.num_children;
    const discountActive = form.num_children >= 2 && subtotal > 0;
    const finalTotal = discountActive ? subtotal * (1 - SIBLING_DISCOUNT) : subtotal;
    const desc = form.booking_option === "full_week"
      ? `£${FULL_WEEK_RATE} × ${form.num_children} ${form.num_children > 1 ? "children" : "child"}`
      : selectedDays.length === 0
        ? "Pick at least one day"
        : `£${DAILY_RATE} × ${selectedDays.length} ${selectedDays.length > 1 ? "days" : "day"} × ${form.num_children} ${form.num_children > 1 ? "children" : "child"}`;
    return { total: finalTotal, breakdown: desc, hasDiscount: discountActive };
  }, [isCamp, form.booking_option, form.num_children, selectedDays]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (day) => {
    setSelectedDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
  };

  const reset = () => {
    setForm({
      ...INITIAL_FORM(defaultService),
      preferred_time: defaultTime || "",
      notes: coachName ? `Preferred coach: ${coachName}` : "",
    });
    setSelectedDays([]);
    setDate(defaultDate);
    setSuccess(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_first_name || !form.parent_last_name || !form.child_first_name || !form.child_last_name || !form.email || !form.phone || !form.child_age) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (isCamp && !effectiveCampId) {
      toast.error("Please pick a camp week.");
      return;
    }
    if (isCamp && form.booking_option === "daily" && selectedDays.length === 0) {
      toast.error("Please pick at least one day for the camp.");
      return;
    }
    if (!isCamp && (form.service_type === "one_to_one" || form.service_type === "small_group")) {
      if (!date) { toast.error("Please choose a date."); return; }
      if (!form.preferred_time) { toast.error("Please choose a time."); return; }
    }
    setLoading(true);
    try {
      const parent_name = `${form.parent_first_name.trim()} ${form.parent_last_name.trim()}`;
      const child_name = `${form.child_first_name.trim()} ${form.child_last_name.trim()}`;
      // 1-to-1 sessions are a flat £35 each; camps use the calculated total
      const chargeable = isCamp ? Number(total.toFixed(2)) : (form.service_type === "one_to_one" ? 35 : null);
      const { data: booking } = await apiClient.post("/bookings", {
        parent_name,
        child_name,
        child_age: parseInt(form.child_age, 10),
        email: form.email,
        phone: form.phone,
        service_type: form.service_type,
        preferred_time: form.preferred_time,
        payment_method: form.payment_method,
        notes: form.notes,
        num_children: parseInt(form.num_children, 10),
        additional_children: form.additional_children,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
        camp_id: effectiveCampId,
        selected_days: isCamp && form.booking_option === "daily" ? selectedDays : [],
        booking_option: isCamp ? form.booking_option : null,
        total_amount: chargeable,
      });

      // Pay online → redirect to Stripe Checkout when there is a chargeable amount
      if (form.payment_method === "online" && chargeable && chargeable > 0) {
        try {
          const { data: checkout } = await apiClient.post("/payments/checkout", {
            booking_id: booking.id,
            origin_url: window.location.origin,
          });
          window.location.href = checkout.url;
          return;
        } catch (err) {
          toast.error("Booking saved, but couldn't start payment. We'll be in touch.");
        }
      }

      setSuccess(true);
      toast.success("Booking received! We'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {trigger ? cloneElement(trigger, { onClick: () => setOpen(true) }) : (
          <button className="btn-gold">Book Now</button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="booking-dialog" className="max-w-2xl bg-slate-50 border-slate-200 text-slate-900 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tight">
            {success ? "Booking Received" : campTitle ? `Book: ${campTitle}` : "Book a Session"}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {success ? "Thanks! Our team will confirm by email within 24 hours." : "Tell us about your child and we'll get them on the pitch."}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 flex flex-col items-center text-center gap-4">
            <CheckCircle2 className="w-16 h-16 text-emerald-500" />
            <p className="text-slate-700 max-w-md">
              {form.payment_method === "online"
                ? "We'll email a secure payment link shortly to confirm your spot."
                : "Pay at the session — please bring cash or card on the day."}
            </p>
            <button data-testid="booking-close-btn" onClick={() => setOpen(false)} className="btn-gold">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="eyebrow text-slate-500">Parent first name *</Label>
                <Input data-testid="booking-parent-first-name" required value={form.parent_first_name} onChange={(e) => update("parent_first_name", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div>
                <Label className="eyebrow text-slate-500">Parent last name *</Label>
                <Input data-testid="booking-parent-last-name" required value={form.parent_last_name} onChange={(e) => update("parent_last_name", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div>
                <Label className="eyebrow text-slate-500">Child first name *</Label>
                <Input data-testid="booking-child-first-name" required value={form.child_first_name} onChange={(e) => update("child_first_name", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div>
                <Label className="eyebrow text-slate-500">Child last name *</Label>
                <Input data-testid="booking-child-last-name" required value={form.child_last_name} onChange={(e) => update("child_last_name", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div>
                <Label className="eyebrow text-slate-500">Child age *</Label>
                <Input data-testid="booking-child-age" required type="number" min="3" max="18" value={form.child_age} onChange={(e) => update("child_age", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div>
                <Label className="eyebrow text-slate-500">Phone *</Label>
                <Input data-testid="booking-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div className="sm:col-span-2">
                <Label className="eyebrow text-slate-500">Email *</Label>
                <Input data-testid="booking-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1 bg-white border-slate-200" />
              </div>
              <div>
                <Label className="eyebrow text-slate-500">Service</Label>
                <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                  <SelectTrigger data-testid="booking-service" className="mt-1 bg-white border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-50 border-slate-200 text-slate-900">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isCamp && (
                <div>
                  <Label className="eyebrow text-slate-500" htmlFor="booking-date-input">Preferred date</Label>
                  <input
                    id="booking-date-input"
                    data-testid="booking-date-input"
                    type="date"
                    min={format(new Date(), "yyyy-MM-dd")}
                    value={date ? format(date, "yyyy-MM-dd") : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) { setDate(null); return; }
                      const [y, m, d] = v.split("-").map(Number);
                      const picked = new Date(y, m - 1, d);
                      const dayName = WEEKDAY_NAMES[picked.getDay()];
                      const dayHasSlots = availSlots.some((s) => s.day === dayName && (!coachName || s.coach === coachName));
                      if (!dayHasSlots) {
                        const who = coachName || "our coaches";
                        toast.error(`No availability with ${who} on that day. Pick another date.`);
                        return;
                      }
                      setDate(picked);
                    }}
                    className="mt-1 w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/40"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {coachName ? `${coachName} coaches on selected days only.` : "Sessions run Mon–Thu."}
                  </p>
                </div>
              )}
              {!isCamp && (
                <div className="sm:col-span-2">
                  <Label className="eyebrow text-slate-500">Preferred time</Label>
                  {date && availableTimes.length > 0 ? (
                    <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
                      <SelectTrigger data-testid="booking-time" className="mt-1 bg-white border-slate-200"><SelectValue placeholder="Select a time" /></SelectTrigger>
                      <SelectContent className="bg-slate-50 border-slate-200 text-slate-900">
                        {availableTimes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div data-testid="booking-time-empty" className="mt-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      {date ? "No coach is available on this day — pick another date." : "Pick a date first to see available times."}
                    </div>
                  )}
                </div>
              )}
              {!isCamp && date && venueForDate(date) && (
                <div className="sm:col-span-2 rounded-xl border-2 border-[#1E3A8A]/15 bg-[#1E3A8A]/5 p-4 flex items-start gap-3" data-testid="booking-venue-notice">
                  <MapPin className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-display uppercase tracking-wider text-[#1E3A8A] text-xs">Session venue</div>
                    <div className="text-slate-700 mt-0.5" data-testid="booking-venue-text">{venueForDate(date)}</div>
                    <div className="text-[11px] text-slate-500 mt-1">Mon &amp; Wed: Bedford Terrace · Tue &amp; Thu: Bede College</div>
                  </div>
                </div>
              )}
            </div>

            {isCamp && (
              <div className="rounded-2xl border-2 border-[#1E3A8A]/15 bg-white p-5 space-y-4" data-testid="camp-pricing-section">
                <div className="flex items-center justify-between">
                  <Label className="eyebrow text-[#1E3A8A]">Camp Booking Options</Label>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Mon–Thu · 9:00–3:00</span>
                </div>

                {/* Week picker (only shown when the dialog wasn't opened from a specific camp card) */}
                {!campId && (
                  <div data-testid="camp-week-picker">
                    <Label className="eyebrow text-slate-500">Which camp week?</Label>
                    {camps.length === 0 ? (
                      <div className="mt-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">Loading camp weeks…</div>
                    ) : (
                      <Select value={pickedCampId || ""} onValueChange={setPickedCampId}>
                        <SelectTrigger data-testid="camp-week-select" className="mt-1 bg-white border-slate-200"><SelectValue placeholder="Choose a week" /></SelectTrigger>
                        <SelectContent className="bg-slate-50 border-slate-200 text-slate-900">
                          {camps.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.title} · {c.start_date} → {c.end_date}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {selectedCamp && (
                      <p className="mt-2 text-xs text-slate-500" data-testid="selected-camp-summary">
                        {selectedCamp.title} at {selectedCamp.location}
                      </p>
                    )}
                  </div>
                )}

                {/* Option: full week vs daily */}
                <RadioGroup value={form.booking_option} onValueChange={(v) => update("booking_option", v)} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label data-testid="option-full-week" className={`cursor-pointer border-2 rounded-xl p-4 flex items-start gap-3 transition ${form.booking_option === "full_week" ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 bg-white"}`}>
                    <RadioGroupItem value="full_week" id="opt-full" className="mt-1" />
                    <div>
                      <div className="font-display uppercase tracking-wider text-sm">Full Week</div>
                      <div className="text-xs text-slate-500 mt-1">All 4 days · best value</div>
                      <div className="font-display text-2xl text-[#1E3A8A] mt-2">£80<span className="text-xs font-normal text-slate-500"> /child</span></div>
                    </div>
                  </label>
                  <label data-testid="option-daily" className={`cursor-pointer border-2 rounded-xl p-4 flex items-start gap-3 transition ${form.booking_option === "daily" ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 bg-white"}`}>
                    <RadioGroupItem value="daily" id="opt-daily" className="mt-1" />
                    <div>
                      <div className="font-display uppercase tracking-wider text-sm">Single Day(s)</div>
                      <div className="text-xs text-slate-500 mt-1">Pick the days that suit you</div>
                      <div className="font-display text-2xl text-[#1E3A8A] mt-2">£25<span className="text-xs font-normal text-slate-500"> /day</span></div>
                    </div>
                  </label>
                </RadioGroup>

                {/* Day picker when daily */}
                {form.booking_option === "daily" && (
                  <div data-testid="day-picker">
                    <Label className="eyebrow text-slate-500">Choose your day(s)</Label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {CAMP_DAYS.map((day) => {
                        const checked = selectedDays.includes(day);
                        return (
                          <button
                            type="button"
                            key={day}
                            data-testid={`day-${day.toLowerCase()}`}
                            onClick={() => toggleDay(day)}
                            className={`px-3 py-3 rounded-lg border-2 text-sm font-display uppercase tracking-wider transition ${checked ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-[#1E3A8A]/50"}`}
                          >
                            {day.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Number of children */}
                <div>
                  <Label className="eyebrow text-slate-500 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Number of children</Label>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        type="button"
                        key={n}
                        data-testid={`children-${n}`}
                        onClick={() => update("num_children", n)}
                        className={`px-3 py-3 rounded-lg border-2 text-base font-display transition ${form.num_children === n ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-[#1E3A8A]/50"}`}
                      >
                        {n}{n === 4 ? "+" : ""}
                      </button>
                    ))}
                  </div>
                  {form.num_children > 1 && (
                    <>
                      <p className="mt-2 text-xs text-[#1E3A8A] font-semibold" data-testid="sibling-discount-note">✓ 10% sibling discount applied</p>
                      <Input
                        data-testid="additional-children"
                        placeholder="Names & ages of other children (e.g. Mia 8, Jack 11)"
                        value={form.additional_children}
                        onChange={(e) => update("additional_children", e.target.value)}
                        className="mt-2 bg-white border-slate-200 text-sm"
                      />
                    </>
                  )}
                </div>

                {/* Total */}
                <div className="border-t border-slate-200 pt-4 flex items-end justify-between">
                  <div>
                    <div className="eyebrow text-slate-500">Total</div>
                    <div className="text-xs text-slate-500 mt-1" data-testid="price-breakdown">{breakdown}{hasDiscount ? " · −10% sibling" : ""}</div>
                  </div>
                  <div className="font-display text-4xl text-[#1E3A8A]" data-testid="total-amount">£{total.toFixed(2)}</div>
                </div>
              </div>
            )}

            {!isCamp && form.service_type === "one_to_one" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="one-to-one-summary">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="eyebrow text-slate-500">Total</div>
                    <div className="text-xs text-slate-500 mt-1">1-to-1 coaching · 60-min session</div>
                  </div>
                  <div className="font-display text-4xl text-[#1E3A8A]" data-testid="total-amount">£35.00</div>
                </div>
              </div>
            )}

            <div>
              <Label className="eyebrow text-slate-500">Payment method *</Label>
              <RadioGroup value={form.payment_method} onValueChange={(v) => update("payment_method", v)} className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label data-testid="payment-online" className={`cursor-pointer border-2 rounded-xl p-4 flex items-start gap-3 ${form.payment_method === "online" ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 bg-white"}`}>
                  <RadioGroupItem value="online" id="pay-online" className="mt-1" />
                  <div>
                    <div className="font-display uppercase tracking-wider text-sm">Pay Online</div>
                    <div className="text-xs text-slate-500 mt-1">Secure card payment link sent to your email.</div>
                  </div>
                </label>
                <label data-testid="payment-at-session" className={`cursor-pointer border-2 rounded-xl p-4 flex items-start gap-3 ${form.payment_method === "at_session" ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 bg-white"}`}>
                  <RadioGroupItem value="at_session" id="pay-session" className="mt-1" />
                  <div>
                    <div className="font-display uppercase tracking-wider text-sm">Pay At Session</div>
                    <div className="text-xs text-slate-500 mt-1">Cash or card accepted on the day.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label className="eyebrow text-slate-500">Notes (optional)</Label>
              <Textarea data-testid="booking-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Allergies, medical conditions, position, goals..." className="mt-1 bg-white border-slate-200" />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-3 text-sm font-semibold uppercase tracking-widest text-slate-700 hover:text-slate-900">Cancel</button>
              <button data-testid="booking-submit-btn" disabled={loading} type="submit" className="btn-gold text-sm">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Confirm Booking"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
